/* eslint-disable react/jsx-key */
/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import * as React from 'react';
import moment from 'moment';

import { PhoneCountryCodeInput } from '../../common/components/phone-number-input';
import DateOfBirthInput, { getDateMask } from '../../common/components/date-of-birth-input';
import SignUpButtonComponent from './components/new-sign-up-button';
import {
    ISignUpEmailVerification,
    ISignUpGivenNameVerification,
    ISignUpItem,
    ISignUpLoading,
    ISignUpSurnameVerification,
    ISignUpViewProps
} from './new-sign-up';

import { format } from '@msdyn365-commerce-modules/utilities';
import { clearNewSignupDataLayerEvent } from '../../common/utils/datalayer';
import { validateEmail } from '../../common/utils/email-utils';
import { MIN_AGE_FOR_SIGNUP } from '../../themes/venchi-v2-1/utils/constants';
import { PasswordRegex } from '@msdyn365-commerce-modules/retail-actions';
import { useGlobalLoader } from '../../common/components/global-loader';
import { createBaseFieldSchemas } from '../../validation';

const SignUpItem: React.FC<ISignUpItem> = ({ wrapper, label, errorMessage, input }) => {
    return (
        <Node {...wrapper}>
            {label}
            {input}
            {errorMessage}
        </Node>
    );
};

const SignUpEmailVerification: React.FC<ISignUpEmailVerification> = ({ email, buttonWrapper, buttons, successMessage, errorMessage }) => {
    return (
        <>
            {successMessage &&
                successMessage.map((message: React.ReactNode) => {
                    return <>{message}</>;
                })}
            <SignUpItem {...email} />
            {errorMessage &&
                errorMessage.map((message: React.ReactNode) => {
                    return <>{message}</>;
                })}
            <Node {...buttonWrapper}>
                {buttons &&
                    buttons.map((button: React.ReactNode) => {
                        return <>{button}</>;
                    })}
            </Node>
        </>
    );
};

const EmailFieldComponent = React.memo<{
    item: ISignUpItem;
    emailVerification: ISignUpEmailVerification;
    props: ISignUpViewProps;
    setShowInvalidMailError: (show: boolean) => void;
    showInvalidMailError: boolean;
    onFieldChange?: () => void;
}>(({ item, emailVerification, props, setShowInvalidMailError, showInvalidMailError, onFieldChange }) => {
    if (emailVerification.isRequired) {
        return (
            <Node {...emailVerification.verificationControlWrapper}>
                <SignUpItem {...item} />
                <SignUpEmailVerification {...emailVerification} />
            </Node>
        );
    } else {
        const emailRepErrorText: string = props.resources.emailRepErrorText ?? null;
        return (
            <>
                <SignUpItem
                    key={item.key}
                    wrapper={item.wrapper}
                    label={item.label}
                    errorMessage={item.errorMessage}
                    input={
                        <input
                            type='email'
                            id={item.key}
                            name={item.key}
                            required
                            onChange={() => {
                                onFieldChange?.();
                                if (typeof window !== 'undefined') {
                                    const email: HTMLInputElement = document.getElementById('email') as HTMLInputElement;
                                    const email_rep: HTMLInputElement = document.getElementById('email_rep') as HTMLInputElement;
                                    const email_rep_error = document.getElementById('text-error') as HTMLParagraphElement;
                                    const email_rep_error_backup = document.getElementById('text-error-backup') as HTMLSpanElement;

                                    setShowInvalidMailError(email.value ? !validateEmail(email.value) : false);

                                    email_rep.pattern = email.value;
                                    email_rep.title = emailRepErrorText;

                                    if (email.value === email_rep.value) {
                                        if (email_rep_error !== null) {
                                            email_rep_error.textContent = ' ';
                                        } else {
                                            email_rep_error_backup.textContent = ' ';
                                        }
                                    } else {
                                        if (email_rep_error !== null) {
                                            email_rep_error.textContent = emailRepErrorText;
                                        } else {
                                            email_rep_error_backup.textContent = emailRepErrorText;
                                        }
                                    }
                                }
                            }}
                            onBlur={() => {
                                if (typeof window !== 'undefined') {
                                    const email: HTMLInputElement = document.getElementById('email') as HTMLInputElement;
                                    setShowInvalidMailError(email.value ? !validateEmail(email.value) : false);
                                }
                            }}
                        />
                    }
                />
                {showInvalidMailError && (
                    <div
                        className='ms-sign-up__item-error error itemLevel ms-sign-up__item-error--postal-code'
                        role='alert'
                        aria-live='assertive'
                        aria-hidden='true'
                    >
                        <p className='ms-sign-up__item-error-text'>{props.resources.invalidEmailError}</p>
                    </div>
                )}
                <div className='ms-sign-up__account-item ms-sign-up__account-item-email_rep entry-item attrEntry'>
                    <label id='email_rep_label' htmlFor='email_rep' className='ms-sign-up__account-item-label'>
                        {props.resources.emailRepeatLabelText ? props.resources.emailRepeatLabelText : 'Repeat your email address *'}
                    </label>
                    <input
                        type='text'
                        id='email_rep'
                        name='email_rep'
                        required
                        maxLength={255}
                        className='ms-sign-up__account-item-input ms-sign-up__account-item-email_rep'
                        onChange={() => {
                            onFieldChange?.();
                            const email: HTMLInputElement = document.getElementById('email') as HTMLInputElement;
                            const email_rep: HTMLInputElement = document.getElementById('email_rep') as HTMLInputElement;
                            const email_rep_error = document.getElementById('text-error') as HTMLParagraphElement;
                            const email_rep_error_backup = document.getElementById('text-error-backup') as HTMLSpanElement;

                            email.pattern = email_rep.value;

                            if (email.value === email_rep.value) {
                                if (email_rep_error !== null) {
                                    email_rep_error.textContent = ' ';
                                } else {
                                    email_rep_error_backup.textContent = ' ';
                                }
                            } else {
                                if (email_rep_error !== null) {
                                    email_rep_error.textContent = emailRepErrorText;
                                } else {
                                    email_rep_error_backup.textContent = emailRepErrorText;
                                }
                            }
                        }}
                        onBlur={() => {
                            const email: HTMLInputElement = document.getElementById('email') as HTMLInputElement;
                            const email_rep: HTMLInputElement = document.getElementById('email_rep') as HTMLInputElement;
                            const email_rep_error = document.getElementById('text-error') as HTMLParagraphElement;
                            const email_rep_error_backup = document.getElementById('text-error-backup') as HTMLSpanElement;

                            if (email.value !== email_rep.value) {
                                if (email_rep_error !== null) {
                                    email_rep_error.textContent = emailRepErrorText;
                                } else {
                                    email_rep_error_backup.textContent = emailRepErrorText;
                                }
                            }
                        }}
                    />
                    <div className='ms-sign-up__item-error error itemLevel' role='alert' aria-live='assertive' aria-hidden='true'>
                        <p id='text-error' style={{ color: '#C13533' }}></p>
                    </div>
                    <span id='text-error-backup' style={{ color: '#C13533' }}></span>
                </div>
            </>
        );
    }
});

const PasswordFieldComponent = React.memo<{
    item: ISignUpItem;
    props: ISignUpViewProps;
    showPassword: { passwordField: boolean; reenterPasswordField: boolean };
    setShowPassword: React.Dispatch<React.SetStateAction<{ passwordField: boolean; reenterPasswordField: boolean }>>;
    setShowInvalidPasswordError: (show: boolean) => void;
    showInvalidPasswordError: boolean;
    isValidPassword: (password: string) => boolean;
    onFieldChange?: () => void;
}>(
    ({
        item,
        props,
        showPassword,
        setShowPassword,
        setShowInvalidPasswordError,
        showInvalidPasswordError,
        isValidPassword,
        onFieldChange
    }) => {
        const passwordRepErrorText: string = props.resources.passwordRepErrorText ?? null;
        return (
            <>
                <SignUpItem
                    key={item.key}
                    wrapper={item.wrapper}
                    label={item.label}
                    errorMessage={item.errorMessage}
                    input={
                        <>
                            <div className='input-group'>
                                <input
                                    type={showPassword.passwordField ? 'text' : 'password'}
                                    className='ms-sign-up__account-item-input ms-sign-up__account-item-newPassword input-group__text'
                                    id={item.key}
                                    name={item.key}
                                    required
                                    onChange={() => {
                                        onFieldChange?.();
                                        if (typeof window !== 'undefined') {
                                            const newPassword: HTMLInputElement = document.getElementById(
                                                'newPassword'
                                            ) as HTMLInputElement;
                                            const reenterPassword: HTMLInputElement = document.getElementById(
                                                'reenterPassword'
                                            ) as HTMLInputElement;
                                            const reenterPassword_error = document.getElementById(
                                                'password-text-error'
                                            ) as HTMLParagraphElement;
                                            const reenterPassword_error_backup = document.getElementById(
                                                'password-text-error-backup'
                                            ) as HTMLSpanElement;

                                            reenterPassword.pattern = newPassword.value;
                                            reenterPassword.title = passwordRepErrorText;

                                            setShowInvalidPasswordError(!isValidPassword(newPassword.value));

                                            if (newPassword.value === reenterPassword.value) {
                                                if (reenterPassword_error !== null) {
                                                    reenterPassword_error.textContent = ' ';
                                                } else {
                                                    reenterPassword_error_backup.textContent = ' ';
                                                }
                                            } else {
                                                if (reenterPassword_error !== null) {
                                                    reenterPassword_error.textContent = passwordRepErrorText;
                                                } else {
                                                    reenterPassword_error_backup.textContent = passwordRepErrorText;
                                                }
                                            }
                                        }
                                    }}
                                    onBlur={() => {
                                        if (typeof window !== 'undefined') {
                                            const newPassword: HTMLInputElement = document.getElementById(
                                                'newPassword'
                                            ) as HTMLInputElement;
                                            setShowInvalidPasswordError(newPassword.value ? !isValidPassword(newPassword.value) : false);
                                        }
                                    }}
                                />
                                <button
                                    onClick={() =>
                                        setShowPassword(curState => ({
                                            ...curState,
                                            passwordField: !curState.passwordField
                                        }))
                                    }
                                    className='input-group__icon-button'
                                    type='button'
                                >
                                    <i
                                        aria-label={
                                            showPassword.passwordField
                                                ? props.resources.hidePasswordLabel
                                                : props.resources.showPasswordLabel
                                        }
                                        className={`input-group__icon ${
                                            showPassword.passwordField ? 'input-group__icon--eye-off' : 'input-group__icon--eye'
                                        }`}
                                    />
                                </button>
                            </div>
                            {showInvalidPasswordError && (
                                <div
                                    className='ms-sign-up__item-error error itemLevel ms-sign-up__item-error--postal-code'
                                    role='alert'
                                    aria-live='assertive'
                                    aria-hidden='true'
                                >
                                    <p className='ms-sign-up__item-error-text'>{props.resources.invalidPasswordInlineError}</p>
                                </div>
                            )}
                        </>
                    }
                />
                <div className='ms-sign-up__account-item ms-sign-up__account-item-reenterPassword entry-item attrEntry'>
                    <label id='reenterPassword_label' htmlFor='reenterPassword' className='ms-sign-up__account-item-label'>
                        {props.resources.confirmPasswordLabelText ? props.resources.confirmPasswordLabelText : 'Confirm password *'}
                    </label>
                    <div className='input-group'>
                        <input
                            type={showPassword.reenterPasswordField ? 'text' : 'password'}
                            id='reenterPassword'
                            name='reenterPassword'
                            required
                            maxLength={255}
                            className='ms-sign-up__account-item-input ms-sign-up__account-item-reenterPassword input-group__text'
                            onChange={() => {
                                onFieldChange?.();
                                const newPassword: HTMLInputElement = document.getElementById('newPassword') as HTMLInputElement;
                                const reenterPassword: HTMLInputElement = document.getElementById('reenterPassword') as HTMLInputElement;
                                const reenterPassword_error = document.getElementById('password-text-error') as HTMLParagraphElement;
                                const reenterPassword_error_backup = document.getElementById(
                                    'password-text-error-backup'
                                ) as HTMLSpanElement;

                                newPassword.pattern = reenterPassword.value;

                                if (newPassword.value === reenterPassword.value) {
                                    if (reenterPassword_error !== null) {
                                        reenterPassword_error.textContent = ' ';
                                    } else {
                                        reenterPassword_error_backup.textContent = ' ';
                                    }
                                } else {
                                    if (reenterPassword_error !== null) {
                                        reenterPassword_error.textContent = passwordRepErrorText;
                                    } else {
                                        reenterPassword_error_backup.textContent = passwordRepErrorText;
                                    }
                                }
                            }}
                            onBlur={() => {
                                const newPassword: HTMLInputElement = document.getElementById('newPassword') as HTMLInputElement;
                                const reenterPassword: HTMLInputElement = document.getElementById('reenterPassword') as HTMLInputElement;
                                const reenterPassword_error = document.getElementById('password-text-error') as HTMLParagraphElement;
                                const reenterPassword_error_backup = document.getElementById(
                                    'password-text-error-backup'
                                ) as HTMLSpanElement;

                                if (newPassword.value !== reenterPassword.value) {
                                    if (reenterPassword_error !== null) {
                                        reenterPassword_error.textContent = passwordRepErrorText;
                                    } else {
                                        reenterPassword_error_backup.textContent = passwordRepErrorText;
                                    }
                                }
                            }}
                        />
                        <button
                            onClick={() =>
                                setShowPassword(curState => ({
                                    ...curState,
                                    reenterPasswordField: !curState.reenterPasswordField
                                }))
                            }
                            className='input-group__icon-button'
                            type='button'
                        >
                            <i
                                aria-label={
                                    showPassword.reenterPasswordField
                                        ? props.resources.hidePasswordLabel
                                        : props.resources.showPasswordLabel
                                }
                                className={`input-group__icon ${
                                    showPassword.reenterPasswordField ? 'input-group__icon--eye-off' : 'input-group__icon--eye'
                                }`}
                            />
                        </button>
                    </div>
                    <div className='ms-sign-up__item-error error itemLevel' role='alert' aria-live='assertive' aria-hidden='true'>
                        <p id='password-text-error' style={{ color: '#C13533' }}></p>
                    </div>
                    <span id='password-text-error-backup' style={{ color: '#C13533' }}></span>
                </div>
            </>
        );
    }
);

const GivenNameFieldComponent = React.memo<{
    item: ISignUpItem;
    givenNameVerification: ISignUpGivenNameVerification;
    onFieldChange?: () => void;
}>(({ item, givenNameVerification, onFieldChange }) => (
    <Node {...givenNameVerification.verificationControlWrapper}>
        <SignUpItem
            {...item}
            input={
                <input
                    {...(React.isValidElement(item.input) && 'props' in item.input ? item.input.props : {})}
                    type='text'
                    id={item.key}
                    name={item.key}
                    onChange={e => {
                        onFieldChange?.();
                        if (React.isValidElement(item.input) && 'onChange' in item.input.props) {
                            item.input.props.onChange?.(e);
                        }
                    }}
                />
            }
        />
    </Node>
));

const SurnameFieldComponent = React.memo<{
    item: ISignUpItem;
    surnameVerification: ISignUpSurnameVerification;
    onFieldChange?: () => void;
}>(({ item, surnameVerification, onFieldChange }) => (
    <Node {...surnameVerification.verificationControlWrapper}>
        <SignUpItem
            {...item}
            input={
                <input
                    {...(React.isValidElement(item.input) && 'props' in item.input ? item.input.props : {})}
                    type='text'
                    id={item.key}
                    name={item.key}
                    onChange={e => {
                        onFieldChange?.();
                        if (React.isValidElement(item.input) && 'onChange' in item.input.props) {
                            item.input.props.onChange?.(e);
                        }
                    }}
                />
            }
        />
    </Node>
));

const ErrorMessage: React.FC<{ message: string; className: string }> = ({ message, className }) => (
    <div className={`ms-sign-up__item-error error itemLevel ${className}`} role='alert' aria-live='assertive' aria-hidden='true'>
        <p className='ms-sign-up__item-error-text'>{message}</p>
    </div>
);

const BirthdayFieldComponent = React.memo<{
    props: ISignUpViewProps;
    locale: string;
    setShowMinAgeError: (show: boolean) => void;
    showMinAgeError: boolean;
    setShowInvalidFormatError: (show: boolean) => void;
    showInvalidFormatError: boolean;
    onFieldChange?: () => void;
}>(({ props, locale, setShowMinAgeError, showMinAgeError, setShowInvalidFormatError, showInvalidFormatError, onFieldChange }) => {
    /**
     * Validates age based on ISO format date (YYYY-MM-DD)
     * Returns true if age is valid (>= MIN_AGE_FOR_SIGNUP)
     */
    const validateAge = React.useCallback((isoDate: string): boolean => {
        if (!isoDate) {
            return false;
        }

        const birthdayDate = moment(isoDate, 'YYYY-MM-DD');
        if (!birthdayDate.isValid()) {
            return false;
        }

        const today = moment();
        const age = today.diff(birthdayDate, 'years');

        return age >= MIN_AGE_FOR_SIGNUP;
    }, []);

    /**
     * Handles date change from the masked input component
     * Receives ISO format (YYYY-MM-DD) from the component
     */
    const handleDateChange = React.useCallback(
        (isoDate: string, isValid: boolean) => {
            if (isValid && isoDate) {
                // Validate age
                const ageValid = validateAge(isoDate);
                setShowMinAgeError(!ageValid);
            } else {
                // Invalid or incomplete date
                setShowMinAgeError(false);
            }
            setShowInvalidFormatError(false);
            onFieldChange?.();
        },
        [validateAge, setShowMinAgeError, setShowInvalidFormatError]
    );

    /**
     * Handles blur event to validate age and format on field exit
     */
    const handleBirthdayBlur = React.useCallback(
        (isoDate: string, isValid: boolean, isFilled?: boolean) => {
            try {
                // Get the hidden ISO field value
                const hiddenField = document.getElementById('extension_Birthday') as HTMLInputElement;
                if (hiddenField && hiddenField.value) {
                    const birthdayDate = moment(hiddenField.value, 'YYYY-MM-DD');
                    if (!birthdayDate.isValid()) {
                        setShowInvalidFormatError(true);
                        setShowMinAgeError(false);
                    } else {
                        const ageValid = validateAge(hiddenField.value);
                        setShowMinAgeError(!ageValid);
                        setShowInvalidFormatError(false);
                    }
                } else if (isoDate || isFilled) {
                    setShowInvalidFormatError(!isValid);
                    setShowMinAgeError(false);
                } else {
                    setShowInvalidFormatError(false);
                    setShowMinAgeError(false);
                }
            } catch (error) {
                setShowInvalidFormatError(false);
                setShowMinAgeError(false);
            }
        },
        [validateAge, setShowMinAgeError, setShowInvalidFormatError]
    );

    return (
        <>
            <div className='ms-sign-up__account-item ms-sign-up__account-item-birthday entry-item attrEntry'>
                <label className='ms-sign-up__account-item-label' htmlFor='extension_Birthday'>
                    {props.resources.birthdayLabelText ? props.resources.birthdayLabelText : null}
                    <span>({getDateMask(locale)})</span>
                </label>
                {/*
                    DateOfBirthInput renders:
                    - A visible text input with locale-aware mask (DD/MM/YYYY or MM/DD/YYYY)
                    - A hidden input with ISO format (YYYY-MM-DD) for backend submission
                */}
                <DateOfBirthInput
                    id='extension_Birthday'
                    name='extension_Birthday'
                    locale={locale}
                    className='ms-sign-up__account-item-input ms-sign-up__account-item-birthday'
                    onChange={handleDateChange}
                    onBlur={handleBirthdayBlur}
                    preventPaste={true}
                />
                {showMinAgeError && (
                    <ErrorMessage message={props.resources.minAgeErrorLabel} className='ms-sign-up__item-error--postal-code' />
                )}
                {showInvalidFormatError && (
                    <ErrorMessage message={props.resources.birthdayErrorMessage} className='ms-sign-up__item-error--invalid-format' />
                )}
            </div>
            <input
                type='text'
                id='extension_Birthday'
                name='extension_Birthday'
                className='ms-sign-up__account-item-input ms-sign-up__account-item-birthday'
                style={{ display: 'none' }}
            />
            {props.config.BirthdayText ? (
                <Msdyn365.RichTextComponent className='extra-text__birthday' text={props.config.BirthdayText} />
            ) : null}
        </>
    );
});

const PostalCodeFieldComponent = React.memo<{
    item: ISignUpItem;
    props: ISignUpViewProps;
    checkPostalCode: (postalCode: string) => void;
    showPostalCodeError: boolean;
    onFieldChange?: () => void;
}>(({ item, props, checkPostalCode, showPostalCodeError, onFieldChange }) => (
    <>
        <SignUpItem
            wrapper={item.wrapper}
            key={item.key}
            label={item.label}
            errorMessage={item.errorMessage}
            input={
                <input
                    type='text'
                    id='postalCode'
                    name='postalCode'
                    className='ms-sign-up__account-item-input ms-sign-up__account-item-postalCode'
                    aria-required='true'
                    maxLength={8}
                    onChange={e => {
                        onFieldChange?.();
                        checkPostalCode(e.target.value);
                    }}
                    onBlur={e => {
                        checkPostalCode(e.target.value);
                    }}
                ></input>
            }
        />
        {showPostalCodeError && (
            <div
                className='ms-sign-up__item-error error itemLevel ms-sign-up__item-error--postal-code'
                role='alert'
                aria-live='assertive'
                aria-hidden='true'
            >
                <p className='ms-sign-up__item-error-text'>{props.resources.postalCodeErrorMessage}</p>
            </div>
        )}
        {props.config.PostalText ? <Msdyn365.RichTextComponent className='extra-text__postalcode' text={props.config.PostalText} /> : null}
    </>
));

const PhoneFieldComponent = React.memo<{
    item: ISignUpItem;
    props: ISignUpViewProps;
    currentChannelCountryRegionISOCode: string;
    phoneErrorMessage: string;
    setPhoneErrorMessage: (message: string) => void;
    onFieldChange?: () => void;
}>(({ item, props, currentChannelCountryRegionISOCode, phoneErrorMessage, setPhoneErrorMessage, onFieldChange }) => (
    <>
        <SignUpItem
            wrapper={item.wrapper}
            key={item.key}
            label={item.label}
            errorMessage={item.errorMessage}
            input={
                <PhoneCountryCodeInput
                    channelCountryRegionISOCode={currentChannelCountryRegionISOCode}
                    isCountryCodeHidden={props.config.hideCountryCodeSelector || false}
                    phoneInputId={item.key}
                    resources={{
                        phoneValidationErrorMin: props.resources.phoneValidationErrorMin,
                        phoneValidationErrorMax: props.resources.phoneValidationErrorMax,
                        phoneValidationErrorNumeric: props.resources.phoneValidationErrorNumeric,
                        phoneValidationErrorRequired: props.resources.phoneValidationErrorRequired
                    }}
                    onPhoneNumberChange={(phoneNumber: string, fullPhoneNumber: string, hasValidationError: boolean) => {
                        onFieldChange?.();
                        if (!hasValidationError) {
                            setPhoneErrorMessage('');
                        } else {
                            setPhoneErrorMessage(props.resources.phoneValidationErrorMessage);
                        }
                        const phoneNumberHidden: HTMLInputElement = document.getElementById('extension_PhoneNumber') as HTMLInputElement;
                        phoneNumberHidden.value = phoneNumber ? fullPhoneNumber : '';
                    }}
                />
            }
        />
        <input
            type='text'
            id='extension_PhoneNumber'
            name='extension_PhoneNumber'
            className='ms-sign-up__account-item-input ms-sign-up__account-item-phone_number'
            style={{ display: 'none' }}
        />
        {phoneErrorMessage && (
            <div className='ms-sign-up__item-error error itemLevel' role='alert' aria-live='assertive' aria-hidden='true'>
                <p className='ms-sign-up__item-error-text' style={{ color: '#C13533' }}>
                    {phoneErrorMessage}
                </p>
            </div>
        )}
        {props.config.PhoneText ? <Msdyn365.RichTextComponent className='extra-text__phone' text={props.config.PhoneText} /> : null}
    </>
));

const MarketingConsentFieldComponent = React.memo<{
    props: ISignUpViewProps;
    locale: string;
    handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onFieldChange?: () => void;
}>(({ props, locale, handleChange, onFieldChange }) => (
    <div className={`inputBox privacy_field_ctn ${props.config.hideMarketing && 'hidden'}`}>
        <label>
            <input
                type='checkbox'
                id='extension_GenericMarketing'
                name='extension_GenericMarketing'
                value={'true'}
                defaultChecked={props.config.preflagMarketing}
                onChange={e => {
                    onFieldChange?.();
                    handleChange(e);
                }}
            />
            {props?.resources?.htmlGenericMarketing && props.config.privacyPolicyURL && locale.toLowerCase() !== 'en-us' ? (
                <p
                    className='privacy-disclaimer-M'
                    dangerouslySetInnerHTML={{
                        __html: format(
                            props.config.useCrossCountryLabels
                                ? props?.resources?.htmlGenericMarketingCrossCountry
                                : props?.resources?.htmlGenericMarketing,
                            props.config.privacyPolicyURL
                        )
                    }}
                />
            ) : (
                props.config.USmarketingText && (
                    <Msdyn365.RichTextComponent className='privacy-disclaimer-M' text={props?.config.USmarketingText} />
                )
            )}
        </label>
    </div>
));

const ProfilingConsentFieldComponent = React.memo<{
    props: ISignUpViewProps;
    profiling_ref: React.RefObject<HTMLInputElement>;
    onFieldChange?: () => void;
}>(({ props, profiling_ref, onFieldChange }) => (
    <div className={`inputBox privacy_field_ctn ${props.config.hideProfiling && 'hidden'}`}>
        <label>
            <input
                type='checkbox'
                id='extension_Profiling'
                name='extension_Profiling'
                value={'true'}
                defaultChecked={props.config.preflagProfiling}
                ref={profiling_ref}
                onChange={() => {
                    onFieldChange?.();
                }}
            />
            {props?.resources?.htmlProfiling && props.config.privacyPolicyURL ? (
                <p
                    className='privacy-disclaimer-P'
                    dangerouslySetInnerHTML={{
                        __html: format(
                            props.config.useCrossCountryLabels
                                ? props?.resources?.htmlProfilingCrossCountry
                                : props?.resources?.htmlProfiling,
                            props.config.privacyPolicyURL
                        )
                    }}
                />
            ) : null}
        </label>
    </div>
));

const ThirdPartyConsentFieldComponent = React.memo<{
    props: ISignUpViewProps;
    onFieldChange?: () => void;
}>(({ props, onFieldChange }) => (
    <div className={`inputBox privacy_field_ctn ${props.config.hideThirdParty && 'hidden'}`}>
        <label>
            <input
                type='checkbox'
                id='extension_ThirdParty'
                name='extension_ThirdParty'
                value={'true'}
                defaultChecked={props.config.preflagThirdParty}
                onChange={() => {
                    onFieldChange?.();
                }}
            />
            {props?.resources?.htmlThirdParty && props.config.privacyPolicyURL ? (
                <p
                    className='privacy-disclaimer-T'
                    dangerouslySetInnerHTML={{
                        __html: format(
                            props.config.useCrossCountryLabels
                                ? props?.resources?.htmlThirdPartyCrossCountry
                                : props?.resources?.htmlThirdParty,
                            props.config.privacyPolicyURL
                        )
                    }}
                />
            ) : null}
        </label>
    </div>
));

const HiddenFieldsComponent = React.memo<{
    regionCRM: string;
    LastConsentSource: string;
    EcomSignUpLegalEntity: string;
}>(({ regionCRM, LastConsentSource, EcomSignUpLegalEntity }) => (
    <>
        <div className={'region_crm_ctn'}>
            <input type='text' name='extension_RegionCRM' id='extension_RegionCRM' value={regionCRM} />
        </div>
        <div className={'lastConsentSource_ctn'}>
            <input type='text' name='extension_LastConsentSource' id='extension_LastConsentSource' value={LastConsentSource} />
        </div>
        <div className={'ecomSignUpLegalEntity_ctn'}>
            <input type='text' name='extension_EcomSignUpLegalEntity' id='extension_EcomSignUpLegalEntity' value={EcomSignUpLegalEntity} />
        </div>
    </>
));

const LocalAccount: React.FC<ISignUpViewProps> = React.memo(props => {
    const {
        localAccount,
        items,
        emailVerification,
        givenNameVerification,
        surnameVerification,
        buttons,
        errorMessage,
        disclaimer
    } = props.signUpLocalAccount;

    const locale = props.locale;
    const regionCRM = props.regionCRM;
    const EcomSignUpLegalEntity = props.EcomSignUpLegalEntity;
    let LastConsentSource: string;

    const currency = props?.signUp?.moduleProps?.context?.request?.channel?.Currency;
    const currentCountry = currency === 'EUR' && locale === 'en-GB' ? 'eu' : locale.replace('-', '');

    const postalCodeValidators: { [key: string]: RegExp } = React.useMemo(
        () => ({
            it: /^\d{5}$/,
            fr: /^\d{5}$|^\d{2} \d{3}$/,
            de: /^\d{5}$/,
            eu: /^[A-Z0-9]{2,10}$/i,
            enGB: /^[A-Z]{1,2}\d[A-Z\d]? \d[A-Z]{2}$/,
            enAU: /^\d{4}$/,
            enSG: /^\d{6}$/,
            // eslint-disable-next-line security/detect-unsafe-regex
            enUS: /^\d{5}(-\d{4})?$/
        }),
        []
    );

    switch (props.LastConsentSource) {
        case 'FinanceOperationEU':
            LastConsentSource = 'Finance&Operation EU';
            break;
        case 'FinanceOperationUS':
            LastConsentSource = 'Finance&Operation US';
            break;
        default:
            LastConsentSource = 'Finance&Operation EU';
    }

    const [showPassword, setShowPassword] = React.useState({
        passwordField: false,
        reenterPasswordField: false
    });

    // This error is shown under the register button.
    const [phoneErrorMessage, setPhoneErrorMessage] = React.useState('');
    const [showPostalCodeError, setShowPostalCodeError] = React.useState<boolean>(false);
    const [showInvalidMailError, setShowInvalidMailError] = React.useState<boolean>(false);
    const [showMinAgeError, setShowMinAgeError] = React.useState<boolean>(false);
    const [showInvalidPasswordError, setShowInvalidPasswordError] = React.useState<boolean>(false);
    const [showInvalidBirthdayFormatError, setShowInvalidBirthdayFormatError] = React.useState<boolean>(false);
    const [showSubmitErrorMessage, setShowSubmitErrorMessage] = React.useState<boolean>(true);

    const checkShowSubmitErrorMessage = React.useCallback(() => {
        if (errorMessage && errorMessage.length > 0) {
            setShowSubmitErrorMessage(true);
        }
    }, [errorMessage?.length]);

    React.useEffect(() => {
        checkShowSubmitErrorMessage();
    }, [errorMessage?.length]);

    const handleAnyFieldChange = () => {
        setShowSubmitErrorMessage(false);
    };

    // Normalize locale for translation context
    const normalizedLocale = React.useMemo(() => {
        if (!locale) {
            return 'en-us';
        }
        // Keep standard locale format (it, en-us, en-GB, etc.) for translation loading
        const lowerLocale = locale.toLowerCase();
        // Map common locale formats to resource file names
        const localeMap: { [key: string]: string } = {
            'it-it': 'it',
            it: 'it',
            'en-us': 'en-us',
            enus: 'en-us',
            'en-gb': 'en-GB',
            engb: 'en-GB',
            'en-au': 'en-AU',
            enau: 'en-AU',
            'en-hk': 'en-HK',
            enhk: 'en-HK',
            'en-sg': 'en-SG',
            ensg: 'en-SG',
            'zh-hant': 'zh-Hant',
            zhhant: 'zh-Hant',
            fr: 'fr',
            de: 'de'
        };
        return localeMap[lowerLocale] || localeMap[lowerLocale.replace('-', '')] || 'en-us';
    }, [locale]);

    // Setup localized field schemas - use state to handle async translation loading
    const [localizedSchemas, setLocalizedSchemas] = React.useState(() => createBaseFieldSchemas());

    // Load translation context asynchronously
    React.useEffect(() => {
        const loadTranslationContext = async () => {
            try {
                // Check if resources have custom validation keys
                const hasCustomKeys = props.resources && props.resources.fieldIncorrectError;

                // Import the createTranslationContext function
                const { createTranslationContext } = await import('../../validation/utils/resources/resource-loader');

                // Create context with custom resources if available
                const context = await createTranslationContext(normalizedLocale, hasCustomKeys ? (props.resources as any) : undefined);
                const schemas = createBaseFieldSchemas(context);
                setLocalizedSchemas(schemas);
            } catch (error) {
                console.warn('Failed to create translation context:', error);
                const fallbackSchemas = createBaseFieldSchemas();
                setLocalizedSchemas(fallbackSchemas);
            }
        };

        if (props.resources) {
            void loadTranslationContext();
        } else {
            // If no resources, use default schemas
            setLocalizedSchemas(createBaseFieldSchemas());
        }
    }, [normalizedLocale, props.resources]);

    // Validation field setup using useCallback (pattern from new-checkout-shipping-address)
    const setupFieldValidation = React.useCallback(() => {
        const cleanupFunctions: (() => void)[] = [];

        const validateSingleField = (fieldKey: string, value: string) => {
            try {
                switch (fieldKey) {
                    case 'firstName':
                    case 'lastName':
                        localizedSchemas.nameSchema.parse(value);
                        break;
                    case 'email':
                        localizedSchemas.emailSchema.parse(value);
                        break;
                    case 'phone':
                        localizedSchemas.phoneSchema.parse(value);
                        break;
                    case 'password':
                        localizedSchemas.passwordSchema.parse(value);
                        break;
                    default:
                        // For other fields, basic validation
                        if (!value.trim()) {
                            throw new Error(props.resources?.genericFieldError || 'This field is required');
                        }
                }
                return { isValid: true, message: '' };
            } catch (error) {
                // Extract Zod error message
                if (error && typeof error === 'object' && 'errors' in error) {
                    const zodError = error as { errors: { message: string }[] };
                    if (Array.isArray(zodError.errors) && zodError.errors.length > 0) {
                        return {
                            isValid: false,
                            message: zodError.errors[0].message
                        };
                    }
                }
                const errorObj = error as { message?: string };
                return {
                    isValid: false,
                    message: errorObj?.message || props.resources?.fieldIncorrectError || 'Invalid value'
                };
            }
        };

        const fieldValidationMappings = [
            { id: 'givenName', key: 'firstName', required: true },
            { id: 'surname', key: 'lastName', required: true },
            { id: 'email', key: 'email', required: true },
            { id: 'email_rep', key: 'confirmEmail', required: false },
            { id: 'newPassword', key: 'password', required: true },
            { id: 'reenterPassword', key: 'confirmPassword', required: true },
            { id: 'extension_PhoneNumber', key: 'phone', required: true },
            { id: 'postalCode', key: 'postalCode', required: false },
            { id: 'extension_Birthday', key: 'birthday', required: false }
        ];

        fieldValidationMappings.forEach(mapping => {
            const field = document.getElementById(mapping.id) as HTMLInputElement;
            if (!field) {
                return;
            }

            const clearError = () => {
                const currentContainer = field.closest('.ms-sign-up__account-item') as HTMLElement;
                if (currentContainer) {
                    currentContainer.classList.remove('field-invalid');
                }
                const errorElement = field.parentElement?.querySelector('.ms-sign-up__item-error');
                if (errorElement) {
                    const errorText = errorElement.querySelector('.ms-sign-up__item-error-text');
                    if (errorText) {
                        errorText.textContent = '';
                    }
                }
            };

            const validateField = () => {
                const value = field.value.trim();
                const currentContainer = field.closest('.ms-sign-up__account-item') as HTMLElement;

                if (currentContainer) {
                    currentContainer.classList.remove('field-invalid');
                }

                if (!value.length && !mapping.required) {
                    return true;
                }

                const validation = validateSingleField(mapping.key, value);
                if (!validation.isValid) {
                    if (currentContainer) {
                        currentContainer.classList.add('field-invalid');
                    }
                    const errorElement = field.parentElement?.querySelector('.ms-sign-up__item-error');
                    if (errorElement) {
                        const errorText = errorElement.querySelector('.ms-sign-up__item-error-text');
                        if (errorText) {
                            errorText.textContent = validation.message;
                        }
                    }
                    return false;
                }

                return true;
            };

            field.addEventListener('input', clearError);
            field.addEventListener('blur', validateField);
            cleanupFunctions.push(() => {
                field.removeEventListener('input', clearError);
                field.removeEventListener('blur', validateField);
            });
        });

        return () => {
            cleanupFunctions.forEach(fn => fn());
        };
    }, [localizedSchemas, props.resources]);

    // Setup field validation on mount
    React.useEffect(() => {
        const cleanup = setupFieldValidation();
        return cleanup;
    }, [setupFieldValidation]);

    const checkPostalCode = React.useCallback((postalCode: string) => {
        if (!postalCode) {
            setShowPostalCodeError(false);
            return;
        }

        setShowPostalCodeError(!postalCodeValidators[currentCountry].test(postalCode));
    }, []);

    const containsCurrency = (password: string) => {
        const pattern = /\p{Sc}/u;
        return pattern.test(password);
    };

    const isValidPassword = (password: string) => {
        console.log(`Regex match: ${PasswordRegex.defaultRegex.test(password)}`);
        console.log(`Contains currency: ${containsCurrency(password)}`);
        return PasswordRegex.defaultRegex.test(password) && !containsCurrency(password);
    };

    if (typeof window !== 'undefined') {
        const signUpButton: HTMLElement = document.getElementById('continue') as HTMLElement;
        if (signUpButton !== undefined) {
            signUpButton.onclick = e => {
                e.preventDefault();
                e.stopPropagation();

                clearNewSignupDataLayerEvent();
                checkShowSubmitErrorMessage();
            };
        }
    }

    const profiling_ref = React.useRef<HTMLInputElement>(null);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (locale.toLowerCase() === 'en-us') {
            const { checked } = e.target;
            profiling_ref.current!.checked = checked;
        }
    };

    const currentChannelCountryRegionISOCode = props.channelCountryRegionISOCode;

    // DONE guest state session variable; check that user ID (the one that's NOT an FC) exists alongside the user not being auth (since if not auth = loyalty)

    // Extract items for conditional rendering (eliminates .map + switch)
    const emailItem = React.useMemo(() => items?.find(item => item.key === 'email'), [items]);
    const passwordItem = React.useMemo(() => items?.find(item => item.key === 'newPassword'), [items]);
    const givenNameItem = React.useMemo(() => items?.find(item => item.key === 'givenName'), [items]);
    const surnameItem = React.useMemo(() => items?.find(item => item.key === 'surname'), [items]);
    const birthdayItem = React.useMemo(() => items?.find(item => item.key === 'extension_Birthday'), [items]);
    const postalCodeItem = React.useMemo(() => items?.find(item => item.key === 'postalCode'), [items]);
    const phoneItem = React.useMemo(() => items?.find(item => item.key === 'phone_number'), [items]);
    const marketingItem = React.useMemo(() => items?.find(item => item.key === 'extension_GenericMarketing'), [items]);
    const profilingItem = React.useMemo(() => items?.find(item => item.key === 'extension_Profiling'), [items]);
    const thirdPartyItem = React.useMemo(() => items?.find(item => item.key === 'extension_ThirdParty'), [items]);
    const regionCrmItem = React.useMemo(() => items?.find(item => item.key === 'extension_RegionCRM'), [items]);
    const consentSourceItem = React.useMemo(() => items?.find(item => item.key === 'extension_LastConsentSource'), [items]);
    const legalEntityItem = React.useMemo(() => items?.find(item => item.key === 'extension_EcomSignUpLegalEntity'), [items]);

    return (
        <Node {...localAccount}>
            {emailItem && (
                <EmailFieldComponent
                    item={emailItem}
                    emailVerification={emailVerification}
                    props={props}
                    setShowInvalidMailError={setShowInvalidMailError}
                    showInvalidMailError={showInvalidMailError}
                    onFieldChange={handleAnyFieldChange}
                />
            )}

            {passwordItem && (
                <PasswordFieldComponent
                    item={passwordItem}
                    props={props}
                    showPassword={showPassword}
                    setShowPassword={setShowPassword}
                    setShowInvalidPasswordError={setShowInvalidPasswordError}
                    showInvalidPasswordError={showInvalidPasswordError}
                    isValidPassword={isValidPassword}
                    onFieldChange={handleAnyFieldChange}
                />
            )}

            {givenNameItem && (
                <GivenNameFieldComponent
                    item={givenNameItem}
                    givenNameVerification={givenNameVerification}
                    onFieldChange={handleAnyFieldChange}
                />
            )}

            {surnameItem && (
                <SurnameFieldComponent item={surnameItem} surnameVerification={surnameVerification} onFieldChange={handleAnyFieldChange} />
            )}

            {birthdayItem && (
                <BirthdayFieldComponent
                    props={props}
                    locale={locale}
                    setShowMinAgeError={setShowMinAgeError}
                    showMinAgeError={showMinAgeError}
                    setShowInvalidFormatError={setShowInvalidBirthdayFormatError}
                    showInvalidFormatError={showInvalidBirthdayFormatError}
                    onFieldChange={handleAnyFieldChange}
                />
            )}

            {postalCodeItem && (
                <PostalCodeFieldComponent
                    item={postalCodeItem}
                    props={props}
                    checkPostalCode={checkPostalCode}
                    showPostalCodeError={showPostalCodeError}
                    onFieldChange={handleAnyFieldChange}
                />
            )}

            {phoneItem && (
                <PhoneFieldComponent
                    item={phoneItem}
                    props={props}
                    currentChannelCountryRegionISOCode={currentChannelCountryRegionISOCode}
                    phoneErrorMessage={phoneErrorMessage}
                    setPhoneErrorMessage={setPhoneErrorMessage}
                    onFieldChange={handleAnyFieldChange}
                />
            )}

            {marketingItem && (
                <MarketingConsentFieldComponent
                    props={props}
                    locale={locale}
                    handleChange={handleChange}
                    onFieldChange={handleAnyFieldChange}
                />
            )}

            {profilingItem && (
                <ProfilingConsentFieldComponent props={props} profiling_ref={profiling_ref} onFieldChange={handleAnyFieldChange} />
            )}

            {thirdPartyItem && <ThirdPartyConsentFieldComponent props={props} onFieldChange={handleAnyFieldChange} />}

            {(regionCrmItem || consentSourceItem || legalEntityItem) && (
                <HiddenFieldsComponent
                    regionCRM={regionCRM}
                    LastConsentSource={LastConsentSource}
                    EcomSignUpLegalEntity={EcomSignUpLegalEntity}
                />
            )}

            {/* Fallback for any other items not handled above */}
            {items &&
                items
                    .filter(
                        item =>
                            ![
                                'email',
                                'newPassword',
                                'givenName',
                                'surname',
                                'extension_Birthday',
                                'postalCode',
                                'phone_number',
                                'extension_GenericMarketing',
                                'extension_Profiling',
                                'extension_ThirdParty',
                                'extension_RegionCRM',
                                'extension_LastConsentSource',
                                'extension_EcomSignUpLegalEntity'
                            ].includes(item.key)
                    )
                    .map((item: ISignUpItem) => <SignUpItem {...item} />)}
            {disclaimer}
            {errorMessage &&
                showSubmitErrorMessage &&
                errorMessage.map((error: React.ReactNode, index: number) => {
                    return <React.Fragment key={index}>{error}</React.Fragment>;
                })}
            {buttons &&
                buttons.map((button: React.ReactNode, index: number) => {
                    // @ts-ignore
                    if (button?.key === 'continue') {
                        const isDisabled =
                            (phoneErrorMessage !== null && phoneErrorMessage !== undefined && phoneErrorMessage !== '') ||
                            showPostalCodeError ||
                            showInvalidMailError ||
                            showMinAgeError ||
                            showInvalidPasswordError ||
                            showInvalidBirthdayFormatError;

                        return (
                            <React.Fragment key={index}>
                                <SignUpButtonComponent
                                    key='continue'
                                    id='continue' // @ts-ignore
                                    className={button.props.className} // @ts-ignore
                                    ariaLabel={button.props.ariaLabel} // @ts-ignore
                                    text={button.props.text}
                                    disabled={isDisabled}
                                />
                            </React.Fragment>
                        );
                    } else {
                        return <React.Fragment key={index}>{button}</React.Fragment>;
                    }
                })}
        </Node>
    );
});

const SignUpLoading: React.FC<ISignUpLoading> = ({ modal, modalBody, icon, message }) => {
    return (
        <Node {...modal}>
            <Node {...modalBody}>
                {icon}
                {message}
            </Node>
        </Node>
    );
};

const SignUpView: React.FC<ISignUpViewProps> = props => {
    const { showLoader, hideLoader } = useGlobalLoader();
    const { signUp, viewState, loading, defaultAADConainer, aadConainer, heading, config } = props;

    React.useEffect(() => {
        if (viewState.isShowLoading) {
            showLoader();
        } else {
            hideLoader();
        }
    }, [viewState.isShowLoading]);

    return (
        <Module {...signUp}>
            {config.includeCloudflareZarazScript && <script src='https://zaraz.venchi.com/cdn-cgi/zaraz/i.js'></script>}
            {viewState.isShowLoading && <Node {...defaultAADConainer} />}
            {viewState.isShowLoading && <SignUpLoading {...loading} />}
            <Node {...aadConainer}>
                {heading}
                <LocalAccount {...props} />
            </Node>
        </Module>
    );
};

export default SignUpView;
